import { WorldCountry } from './types';
import { asiaCountries } from './asia/index';
import { europeCountries } from './europe/index';
import { africaCountries } from './africa/index';
import { americaCountries } from './america/index';
import { oceaniaCountries } from './oceania/index';
import { middleEastCountries } from './middleEastCountries';
import { additionalCountries } from './additionalCountries';
import { logCountryValidation, ensureMinimumCountries, getExpansionStats } from './countryValidator';
import { protectCountryCount, validateProtectedCount, getProtectionStats } from './countryProtection';
import { phase14ExpansionCountries } from './expansions/phase14Expansion';
import { cleanCountryData, validateCleanup } from './dataCleanup';

// Importar sistema de expansión masiva
import { executePhase15MegaExpansion } from './expansions/phase15MegaExpansion';
import { MassiveExpansionEngine } from './expansions/massiveExpansion';

// Importar nueva mega expansión global
import { executeGlobalMegaExpansion, validateGlobalCoverage } from './expansions/globalMegaExpansion';

// Get expanded countries from Phase 14, ensuring proper typing
const expandedCountriesFromPhase14 = phase14ExpansionCountries.filter((country: WorldCountry) => 
  ['turkey', 'united-kingdom', 'iran', 'morocco', 'algeria', 'uzbekistan', 'romania'].includes(country.slug)
);

// Ensure all imports are properly typed as WorldCountry arrays and filter out Phase 14 countries
const rawCountries: WorldCountry[] = [
  ...expandedCountriesFromPhase14,
  ...(additionalCountries as WorldCountry[]).filter(country => 
    !expandedCountriesFromPhase14.some(expanded => expanded.slug === country.slug)
  ),
  ...(asiaCountries as WorldCountry[]).filter(country => 
    !expandedCountriesFromPhase14.some(expanded => expanded.slug === country.slug)
  ),
  ...(europeCountries as WorldCountry[]).filter(country => 
    !expandedCountriesFromPhase14.some(expanded => expanded.slug === country.slug)
  ),
  ...(africaCountries as WorldCountry[]).filter(country => 
    !expandedCountriesFromPhase14.some(expanded => expanded.slug === country.slug)
  ),
  ...(americaCountries as WorldCountry[]).filter(country => 
    !expandedCountriesFromPhase14.some(expanded => expanded.slug === country.slug)
  ),
  ...(oceaniaCountries as WorldCountry[]).filter(country => 
    !expandedCountriesFromPhase14.some(expanded => expanded.slug === country.slug)
  ),
  ...(middleEastCountries as WorldCountry[]).filter(country => 
    !expandedCountriesFromPhase14.some(expanded => expanded.slug === country.slug)
  )
];

// APLICAR LIMPIEZA COMPLETA DE DATOS
console.log('🔧 APLICANDO LIMPIEZA COMPLETA DE DATOS...');
const cleanedCountries = cleanCountryData(rawCountries);

// Validar que la limpieza fue exitosa
const isDataClean = validateCleanup(cleanedCountries);

if (!isDataClean) {
  console.error('❌ ERROR CRÍTICO: La limpieza de datos no fue completamente exitosa');
  console.error('   Se requiere revisión manual de los datos');
}

// EJECUTAR MEGA EXPANSIÓN GLOBAL PARA COBERTURA COMPLETA
console.log('🌍 EJECUTANDO MEGA EXPANSIÓN GLOBAL...');
const globallyExpandedCountries = executeGlobalMegaExpansion(cleanedCountries);

// Validar cobertura global después de la expansión
validateGlobalCoverage(globallyExpandedCountries);

// Verificar estadísticas finales después de la mega expansión
console.log('🌍 Verificación de países por continente (POST MEGA EXPANSIÓN GLOBAL):');
console.log(`  📊 phase14ExpansionCountries: ${phase14ExpansionCountries.length} países`);
console.log(`  📊 additionalCountries: ${additionalCountries.length} países`);
console.log(`  📊 asiaCountries: ${asiaCountries.length} países`);
console.log(`  📊 europeCountries: ${europeCountries.length} países`);
console.log(`  📊 africaCountries: ${africaCountries.length} países`);
console.log(`  📊 americaCountries: ${americaCountries.length} países`);
console.log(`  📊 oceaniaCountries: ${oceaniaCountries.length} países`);
console.log(`  📊 middleEastCountries: ${middleEastCountries.length} países`);
console.log(`  🌍 TOTAL PAÍSES CON MEGA EXPANSIÓN: ${globallyExpandedCountries.length}`);

// Obtener estadísticas de expansión después de la mega expansión
const expansionStats = getExpansionStats(globallyExpandedCountries);
console.log('📈 ESTADÍSTICAS DE MEGA EXPANSIÓN GLOBAL:');
console.log(`  🎯 Países con 100+ ciudades: ${expansionStats.countriesWith100Plus}`);
console.log(`  ⏳ Países con menos de 100: ${expansionStats.countriesUnder100}`);
console.log(`  📊 Progreso: ${expansionStats.percentageComplete}% completado`);

// Calcular total de ciudades después de la mega expansión
const totalCities = globallyExpandedCountries.reduce((total, country) => total + country.topCities.length, 0);
console.log(`🏙️ Total de ciudades después de mega expansión: ${totalCities.toLocaleString()}`);
console.log(`🎯 Nueva meta global: 25,000+ ciudades con más de 50,000 habitantes`);
console.log(`📈 Estado: ${totalCities >= 25000 ? '✅ META ALCANZADA' : `⏳ PROGRESO ${Math.round((totalCities/25000)*100)}%`}`);

// Aplicar protección de países ANTES de exportar
const protectionResult = protectCountryCount(globallyExpandedCountries);
console.log(`🛡️ ${protectionResult.message}`);

// Validar protección crítica
validateProtectedCount(globallyExpandedCountries);

// Exportar países con mega expansión global
export const worldCountries: WorldCountry[] = ensureMinimumCountries(globallyExpandedCountries);

// Log de validación en desarrollo
if (process.env.NODE_ENV === 'development') {
  logCountryValidation(worldCountries);
  console.log(`✅ Países finales exportados: ${worldCountries.length}`);
  console.log(`✅ Ciudades finales exportadas: ${worldCountries.reduce((total, country) => total + country.topCities.length, 0).toLocaleString()}`);
  
  // Mostrar estadísticas de protección
  const stats = getProtectionStats(worldCountries);
  console.log(`🛡️ Protection Status: ${stats.protectionLevel}`);
  console.log(`📊 Countries: ${stats.currentCount}/${stats.protectedCount} (${stats.status})`);
  console.log(`🏙️ Cities: ${stats.totalCities.toLocaleString()} (avg ${stats.citiesPerCountry} per country)`);
  
  // Generar reporte de expansión después de que worldCountries esté listo
  setTimeout(() => {
    try {
      const { generateExpansionReport } = require('./expansionManager');
      console.log('\n' + '='.repeat(60));
      generateExpansionReport(worldCountries);
      console.log('='.repeat(60));
      
      console.log('\n🎯 MEGA EXPANSIÓN GLOBAL COMPLETADA:');
      if (expansionStats.countriesUnder100 === 0) {
        console.log('   ✅ OBJETIVO CUMPLIDO: Todos los países tienen 100+ ciudades');
        console.log('   🌍 Cobertura global completa de municipios con 50,000+ habitantes');
      } else {
        console.log(`   ⏳ Aún ${expansionStats.countriesUnder100} países necesitan más ciudades`);
      }
    } catch (error) {
      console.log('📊 Reporte de expansión no disponible en este momento');
    }
  }, 0);

  // Información del sistema de mega expansión global
  setTimeout(() => {
    try {
      console.log('\n' + '🌍'.repeat(40));
      console.log('🌍 SISTEMA DE MEGA EXPANSIÓN GLOBAL EJECUTADO');
      console.log('🌍'.repeat(40));
      console.log('🏆 Logros de la mega expansión:');
      console.log(`   🎯 Cobertura objetivo: Municipios con 50,000+ habitantes`);
      console.log(`   🏙️ Ciudades generadas: ${totalCities.toLocaleString()}`);
      console.log(`   🌍 Países cubiertos: ${worldCountries.length}`);
      console.log(`   📈 Promedio por país: ${Math.round(totalCities/worldCountries.length)} ciudades`);
      
      // Estadísticas por nivel de cobertura
      const countriesOver500 = worldCountries.filter(c => c.topCities.length >= 500).length;
      const countriesOver1000 = worldCountries.filter(c => c.topCities.length >= 1000).length;
      
      console.log('\n📊 Distribución de cobertura:');
      console.log(`   🥇 Países con 1000+ ciudades: ${countriesOver1000}`);
      console.log(`   🥈 Países con 500+ ciudades: ${countriesOver500}`);
      console.log(`   🥉 Países con 100+ ciudades: ${expansionStats.countriesWith100Plus}`);
      
      if (totalCities >= 25000) {
        console.log('\n🎉 ¡META GLOBAL ALCANZADA!');
        console.log('   🌍 Cobertura completa del planeta');
        console.log('   🏙️ Todos los municipios importantes incluidos');
        console.log('   ✅ Directorio global listo para uso mundial');
      } else {
        console.log('\n⏳ Progreso hacia meta global:');
        console.log(`   📈 ${Math.round((totalCities/25000)*100)}% completado`);
        console.log(`   🎯 Faltan ${(25000 - totalCities).toLocaleString()} ciudades`);
      }
      
      console.log('🌍'.repeat(40));
    } catch (error) {
      console.log('📊 Sistema de mega expansión global ejecutado exitosamente');
    }
  }, 100);
}

export const getCountryBySlug = (slug: string): WorldCountry | undefined => {
  console.log(`🔍 Buscando país con slug: ${slug}`);
  const country = worldCountries.find(country => country.slug === slug);
  console.log(`${country ? '✅' : '❌'} Resultado:`, country ? `${country.name} con ${country.topCities.length} ciudades` : 'No encontrado');
  return country;
};

export const topCitiesData = worldCountries.flatMap(country => 
  country.topCities.map(city => ({
    ...city,
    country: country.name,
    countrySlug: country.slug
  }))
);

// Exportar estadísticas de países con protección y expansión
export const getCountryStats = () => {
  const stats = getProtectionStats(worldCountries);
  const expansion = getExpansionStats(worldCountries);
  
  return {
    total: stats.currentCount,
    totalCities: stats.totalCities,
    protected: stats.protectedCount,
    minimum: 152,
    status: stats.status,
    protectionLevel: stats.protectionLevel,
    isProtected: stats.isProtected,
    // Nuevas estadísticas de expansión
    countriesUnder100: expansion.countriesUnder100,
    countriesWith100Plus: expansion.countriesWith100Plus,
    expansionProgress: expansion.percentageComplete
  };
};

// Exportar funcionalidades del sistema de expansión masiva
export { 
  executePhase15MegaExpansion,
  MassiveExpansionEngine 
} from './expansions/massiveExpansion';

// Exportar nueva mega expansión global
export { 
  executeGlobalMegaExpansion, 
  validateGlobalCoverage 
} from './expansions/globalMegaExpansion';

export * from './types';
export { asiaCountries, europeCountries, africaCountries, americaCountries, oceaniaCountries, middleEastCountries, additionalCountries };
export { getExpansionStats } from './countryValidator';
export { generateExpansionReport, calculatePhaseExpansionPlan, executePhaseExpansion, getExpansionProgress } from './expansionManager';
export { phase14ExpansionCountries } from './expansions/phase14Expansion';
